// Общие кусочки магазина: значок Telegram Stars, цена в ★ и в талонах с ориентиром (ux/08 правило 2, E12-T22),
// кнопка с удержанием 1 с (§2.2). Значок звезды — фирменный Telegram Stars (золотая звезда со скруглёнными
// лучами), а не ICO-STAR игры: правило 2 ux/08 «рядом со значком звезды Telegram».
import { useEffect, useRef, useState } from 'preact/hooks';
import { useApp } from '../app/app.js';
import { num } from '../i18n/format.js';
import { t as tr } from '../i18n/t.js';
import { I } from '../assets/icons.js';
import type { Hint } from './store-model.js';
import type { Item } from '../content/types.js';
import { STAT_NAMES, itemName } from '../content/catalog.js';
import { equipped } from '../state/game.js';
import { compareStats, fmtStat } from './inventory.js';
import '../ui/store.css';

let gid = 0;

/** Значок Telegram Stars (XTR). */
export function StarsIcon(p: { size?: number; label?: string }) {
  const id = useRef(`tgs${++gid}`).current;
  const s = p.size ?? 16;
  return (
    <svg class="tg-star" width={s} height={s} viewBox="0 0 24 24" role={p.label ? 'img' : undefined} aria-label={p.label} aria-hidden={p.label ? undefined : true}>
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stop-color="#FFE071" />
          <stop offset="1" stop-color="#F7A31B" />
        </linearGradient>
      </defs>
      <path
        d="M12 1.6c.5 0 .95.28 1.17.73l2.6 5.27 5.82.85c.5.07.9.42 1.06.9.15.47.02 1-.33 1.34l-4.2 4.1.99 5.8c.08.49-.12.98-.52 1.27-.4.29-.93.33-1.37.1L12 19.24l-5.2 2.73c-.45.23-.97.2-1.38-.1-.4-.3-.6-.78-.51-1.27l.99-5.8-4.2-4.1c-.36-.34-.49-.87-.34-1.34.16-.48.57-.83 1.06-.9l5.82-.85 2.6-5.27c.22-.45.68-.73 1.17-.73z"
        fill={`url(#${id})`}
      />
      <path d="M12 5.2l1.9 3.9 4.3.62-3.1 3.03.73 4.28L12 15.02" fill="#fff" opacity="0.28" />
    </svg>
  );
}

/** «150 ★» — число и фирменный значок. */
export function Stars(p: { n: number; size?: number }) {
  return <span class="stars-price">{num(p.n)}<StarsIcon size={p.size ?? 16} label={tr('CL-STORE-STARS-A11Y')} /></span>;
}

/** Мелкая строка ориентира: «≈ {n} ★ · ≈ {r} ₽». Рубли — только со знаком «≈» (E12-T22). */
export function HintLine(p: { hint: Hint; testId?: string }) {
  const { stars, rub } = p.hint;
  if (stars === null && rub === null) return null;
  return (
    <span class="price-hint" data-testid={p.testId ?? 'price-hint'}>
      {stars !== null ? <>{tr('CL-STORE-APPROX')}{' '}<Stars n={stars} size={12} /></> : null}
      {stars !== null && rub !== null ? ' · ' : null}
      {rub !== null ? tr('CL-STORE-RUB', { n: num(rub) }) : null}
    </span>
  );
}

/** Сноска к рублям — один раз на экран (правило 2). */
export function RubNote() {
  return <p class="small muted rub-note">{tr('CL-STORE-RUB-NOTE')}</p>;
}

/** Цена в талонах: «60 талонов» значком и ориентир под ней. */
export function TalonPrice(p: { n: number; hint: Hint }) {
  return (
    <span class="talon-price col">
      <span class="row" style={{ gap: 4, alignItems: 'center' }}><I n="talons" size={16} /><b>{num(p.n)}</b></span>
      <HintLine hint={p.hint} />
    </span>
  );
}

/**
 * Кнопка покупки с удержанием 1 с для покупок дороже 1 000 талонов (§2.2): полоса заполняется, пока палец на
 * кнопке; отпустил раньше — ничего не происходит. Без удержания — обычный тап.
 */
export function HoldButton(p: { text: string; hold: boolean; disabled?: boolean; testId?: string; onDone: () => Promise<unknown> | unknown; tone?: 'accent' | 'outline' }) {
  const app = useApp();
  const [prog, setProg] = useState(0);
  const [busy, setBusy] = useState(false);
  const t = useRef<{ start: number; raf: number } | null>(null);
  useEffect(() => () => { if (t.current) cancelAnimationFrame(t.current.raf); }, []);
  const fire = async () => {
    if (busy) return;
    setBusy(true);
    try { await p.onDone(); } finally { setBusy(false); setProg(0); }
  };
  const down = () => {
    if (!p.hold || p.disabled || busy) return;
    const start = performance.now();
    const tick = () => {
      const f = Math.min(1, (performance.now() - start) / 1000);
      setProg(f);
      if (f >= 1) { t.current = null; app.haptic('medium'); void fire(); return; }
      t.current = { start, raf: requestAnimationFrame(tick) };
    };
    t.current = { start, raf: requestAnimationFrame(tick) };
  };
  const up = () => { if (t.current) { cancelAnimationFrame(t.current.raf); t.current = null; setProg(0); } };
  return (
    <button
      class={`btn ${p.tone ?? 'accent'} hold-btn`}
      disabled={p.disabled || busy}
      aria-busy={busy}
      data-testid={p.testId}
      onPointerDown={down}
      onPointerUp={up}
      onPointerLeave={up}
      onPointerCancel={up}
      onClick={() => { if (!p.hold) void fire(); }}
      onKeyDown={(e) => { if (p.hold && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); void fire(); } }}
    >
      {p.hold ? <span class="hold-fill" style={{ transform: `scaleX(${prog})` }} aria-hidden /> : null}
      {busy ? <span class="spinner" aria-hidden /> : null}
      <span class="hold-text">{p.text}</span>
      {p.hold && !busy ? <span class="hold-tip">{tr('CL-STORE-HOLD')}</span> : null}
    </button>
  );
}

/**
 * Характеристики предмета до покупки (витрина, лавка; Р-88) и сравнение с надетым в том же слоте:
 * разница зелёным или красным, как в MOD-ITEM (ux/05 §4).
 */
export function StatsCompare(p: { stats: Item['stats']; slot: Item['slot']; compact?: boolean; testId?: string }) {
  const worn = equipped.value.find((e) => e.slot === p.slot);
  const rows = compareStats({ stats: p.stats } as Item, worn);
  if (!rows.length) return null;
  if (p.compact) {
    return (
      <span class="small stats-line" data-testid={p.testId}>
        {rows.map(({ k, v, diff }) => <span key={k}>{STAT_NAMES[k]} {fmtStat(k, v)}{worn && diff !== 0 ? <span class={diff > 0 ? 'pos' : 'neg'}> {diff > 0 ? '▲' : '▼'}</span> : null}</span>)}
      </span>
    );
  }
  return (
    <div class="card col" style={{ gap: 4 }} data-testid={p.testId}>
      {rows.map(({ k, v, diff }) => (
        <div key={k} class="row" style={{ justifyContent: 'space-between' }}>
          <span>{STAT_NAMES[k]}</span>
          <span><b>{fmtStat(k, v)}</b>{worn && diff !== 0 ? <span class={diff > 0 ? 'pos' : 'neg'}> {diff > 0 ? '+' : '−'}{fmtStat(k, Math.abs(diff))}</span> : null}</span>
        </div>
      ))}
      {worn ? <span class="small muted">{tr('CL-SCREENS-INVENTORY-17')}{' '}{itemName(worn.defId)}</span> : null}
    </div>
  );
}
